/**
 * Livro caixa do consultório: entradas e saídas (gravadas no banco pela API).
 *
 * As entradas nascem principalmente do atendimento (botão de comparecimento na
 * agenda) e dos serviços pré-cadastrados; as saídas usam categorias
 * pré-formatadas que o usuário pode estender.
 */

export type CashEntryType = 'entrada' | 'saida';

export type CashEntry = {
  id: string;
  type: CashEntryType;
  /** Data no formato ISO curto (YYYY-MM-DD) para comparação e filtro estáveis. */
  date: string;
  /** Valor sempre positivo, em reais. */
  amount: number;
  category: string;
  description?: string;
  patientId?: string;
  patientName?: string;
  /** Evento da agenda que originou a entrada, quando aplicável. */
  appointmentId?: string;
  /** Bloco #55: profissional e serviço do atendimento de origem. */
  professionalId?: string;
  professionalName?: string;
  serviceName?: string;
  /** `agenda` = gerado pelo comparecimento; `manual` = lançado pelo usuário. */
  source: 'agenda' | 'manual';
  createdAt: string;
};

export const STORAGE_KEY = 'allodos-cash-book';
export const CUSTOM_INCOME_CATEGORIES_KEY = 'allodos-cash-income-categories';
export const CUSTOM_EXPENSE_CATEGORIES_KEY = 'allodos-cash-expense-categories';

/** Categorias de saída pré-formatadas pedidas no roteiro. */
export const DEFAULT_EXPENSE_CATEGORIES = [
  'Aluguel',
  'Condomínio',
  'Internet',
  'Materiais de escritório',
  'Limpeza',
  'Manutenção',
  'Anuidade do conselho de classe',
] as const;

/** Categorias de entrada que não vêm de um serviço cadastrado. */
export const DEFAULT_INCOME_CATEGORIES = ['Sessão', 'Falta cobrada', 'Outros recebimentos'] as const;

export const isCashEntry = (value: unknown): value is CashEntry => {
  if (!value || typeof value !== 'object') return false;
  const e = value as Partial<CashEntry>;
  return (
    typeof e.id === 'string' &&
    (e.type === 'entrada' || e.type === 'saida') &&
    typeof e.date === 'string' &&
    typeof e.amount === 'number' &&
    Number.isFinite(e.amount) &&
    typeof e.category === 'string'
  );
};

/** `YYYY-MM` de uma data ISO curta. */
export function monthKey(date: string): string {
  return date.slice(0, 7);
}

export function currentMonthKey(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export type CashFilters = {
  type?: CashEntryType;
  /** `YYYY-MM`; ignorado quando `from`/`to` estão preenchidos. */
  month?: string;
  from?: string;
  to?: string;
  patientId?: string;
  category?: string;
  /** Bloco #66 */
  professionalId?: string;
  serviceName?: string;
};

/**
 * Aplica os filtros das sub-abas: período (mês ou intervalo), paciente
 * (entradas) e categoria (saídas).
 */
export function filterEntries(entries: CashEntry[], filters: CashFilters = {}): CashEntry[] {
  const { type, month, from, to, patientId, category, professionalId, serviceName } = filters;

  return (entries ?? []).filter((entry) => {
    if (type && entry.type !== type) return false;
    if (patientId && entry.patientId !== patientId) return false;
    if (category && entry.category !== category) return false;
    // Bloco #66: lançamento sem profissional/serviço (ex.: despesa manual) não
    // aparece quando se filtra por eles.
    if (professionalId && entry.professionalId !== professionalId) return false;
    if (serviceName && entry.serviceName !== serviceName) return false;

    if (from && entry.date < from) return false;
    if (to && entry.date > to) return false;
    if (!from && !to && month && monthKey(entry.date) !== month) return false;

    return true;
  });
}

export type CashSummary = {
  totalEntradas: number;
  totalSaidas: number;
  saldo: number;
  countEntradas: number;
  countSaidas: number;
};

export function summarize(entries: CashEntry[]): CashSummary {
  return (entries ?? []).reduce<CashSummary>(
    (acc, entry) => {
      if (entry.type === 'entrada') {
        acc.totalEntradas = round2(acc.totalEntradas + entry.amount);
        acc.countEntradas += 1;
      } else {
        acc.totalSaidas = round2(acc.totalSaidas + entry.amount);
        acc.countSaidas += 1;
      }
      acc.saldo = round2(acc.totalEntradas - acc.totalSaidas);
      return acc;
    },
    { totalEntradas: 0, totalSaidas: 0, saldo: 0, countEntradas: 0, countSaidas: 0 },
  );
}

/** Totais por categoria, do maior para o menor — alimenta os gráficos. */
export function totalsByCategory(entries: CashEntry[]): { category: string; total: number }[] {
  const map = new Map<string, number>();
  for (const entry of entries ?? []) {
    map.set(entry.category, round2((map.get(entry.category) ?? 0) + entry.amount));
  }
  return [...map.entries()]
    .map(([category, total]) => ({ category, total }))
    .sort((a, b) => b.total - a.total);
}

/** Série diária do mês, para o gráfico da aba Geral. */
export function dailySeries(entries: CashEntry[], month: string): {
  day: string;
  entradas: number;
  saidas: number;
}[] {
  const map = new Map<string, { entradas: number; saidas: number }>();

  for (const entry of entries ?? []) {
    if (monthKey(entry.date) !== month) continue;
    const bucket = map.get(entry.date) ?? { entradas: 0, saidas: 0 };
    if (entry.type === 'entrada') bucket.entradas = round2(bucket.entradas + entry.amount);
    else bucket.saidas = round2(bucket.saidas + entry.amount);
    map.set(entry.date, bucket);
  }

  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, totals]) => ({ day: date.slice(8), ...totals }));
}

/** Meses com lançamentos, do mais recente para o mais antigo. */
export function availableMonths(entries: CashEntry[], now: Date = new Date()): string[] {
  const months = new Set((entries ?? []).map((e) => monthKey(e.date)));
  months.add(currentMonthKey(now));
  return [...months].sort((a, b) => b.localeCompare(a));
}

export function formatMonthLabel(month: string): string {
  const [year, m] = month.split('-');
  const names = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
  ];
  const name = names[Number(m) - 1];
  return name ? `${name} de ${year}` : month;
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
}

/** Converte "1.234,56", "1234.56" ou "R$ 80" em número. */
export function parseAmount(input: string | number): number {
  if (typeof input === 'number') return Number.isFinite(input) ? round2(input) : 0;
  const cleaned = String(input)
    .replace(/[^\d,.-]/g, '')
    .replace(/\.(?=\d{3}(\D|$))/g, '')
    .replace(',', '.');
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? round2(parsed) : 0;
}

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/** Data de hoje em ISO curto, respeitando o fuso local. */
export function todayIso(now: Date = new Date()): string {
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Navegador: só para a migração única ao banco e para as categorias criadas
// pelo usuário. Os lançamentos ficam na API (services/cashBookApi.ts).
// ---------------------------------------------------------------------------

/** Marca que os lançamentos deste navegador já subiram para o banco. */
export const MIGRATED_KEY = 'allodos-cash-book-migrated';

/** Lançamentos que ficaram salvos no navegador antes do Livro Caixa ir para o banco. */
export function loadEntries(): CashEntry[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    const parsed = saved ? JSON.parse(saved) : [];
    return Array.isArray(parsed) ? parsed.filter(isCashEntry) : [];
  } catch {
    return [];
  }
}

export function loadCustomCategories(type: CashEntryType): string[] {
  const key = type === 'entrada' ? CUSTOM_INCOME_CATEGORIES_KEY : CUSTOM_EXPENSE_CATEGORIES_KEY;
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed.filter((c): c is string => typeof c === 'string') : [];
  } catch {
    return [];
  }
}

export function saveCustomCategories(type: CashEntryType, categories: string[]): void {
  const key = type === 'entrada' ? CUSTOM_INCOME_CATEGORIES_KEY : CUSTOM_EXPENSE_CATEGORIES_KEY;
  localStorage.setItem(key, JSON.stringify(categories));
}
